/**
 * Order Blocks (OB)
 *
 * Smart Money Concept: Last opposing candle before a significant move.
 * Bullish OB = last bearish candle before a strong up-move.
 * Bearish OB = last bullish candle before a strong down-move.
 *
 * Category: Advanced | Pane: Overlay
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";

export const orderBlocksIndicator: IndicatorDefinition = {
  id: "order_blocks",
  name: "Order Blocks",
  shortName: "OB",
  category: "advanced",
  pane: "overlay",
  params: [
    { key: "lookback", label: "Lookback", type: "number", default: 10, min: 3, max: 50 },
    { key: "threshold", label: "Move Threshold %", type: "number", default: 1, min: 0.1, max: 10, step: 0.1 },
  ],
  plots: [
    { key: "bullOB", label: "Bull OB", color: "#4CAF50", style: "circles", lineWidth: 4 },
    { key: "bearOB", label: "Bear OB", color: "#F44336", style: "circles", lineWidth: 4 },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { lookback, threshold } = params;
    const bullData: { time: number; value: number }[] = [];
    const bearData: { time: number; value: number }[] = [];

    for (let i = lookback; i < candles.length; i++) {
      // Check for significant move in the last `lookback` candles
      const movePct = (candles[i].close - candles[i - lookback].close) / candles[i - lookback].close * 100;

      if (Math.abs(movePct) >= threshold) {
        // Find the last opposing candle
        if (movePct > 0) {
          // Bullish move: find last bearish candle
          for (let j = i - 1; j >= i - lookback; j--) {
            if (candles[j].close < candles[j].open) {
              bullData.push({ time: candles[j].time, value: candles[j].low });
              break;
            }
          }
        } else {
          // Bearish move: find last bullish candle
          for (let j = i - 1; j >= i - lookback; j--) {
            if (candles[j].close > candles[j].open) {
              bearData.push({ time: candles[j].time, value: candles[j].high });
              break;
            }
          }
        }
      }
    }

    return [
      { key: "bullOB", data: bullData },
      { key: "bearOB", data: bearData },
    ];
  },
};
